import type { ReactNode } from 'react'
import { Link, Navigate } from 'react-router'
import { Card } from '../../ui'
import { personOf, useSession } from './session'
import './live.css'

/**
 * The AI page's gate (the AI area, docs/ai-posture-design.md).
 *
 * Where this build has no AI area, the address goes to Recommendations. Off Monitor (owner, 2026-10-11: the whole AI
 * area is Monitor only), nothing is assessed, so the page says that instead of a findings list whose zero nobody
 * measured. On Monitor it is the findings list, then the inventory of AI applications and agents.
 */
export function AiArea({ children }: { children: ReactNode }) {
  return (
    <AiGate>
      {children}
      <AiAssetsLink />
    </AiGate>
  )
}

/**
 * The AI area's gate, shared by the AI page and AI assets: where this build has no AI area the address goes to
 * Recommendations, and off Monitor the page says AI is part of Monitor instead of showing a zero nobody measured.
 */
export function AiGate({ children }: { children: ReactNode }) {
  const session = useSession()
  if (session.state === 'loading') return <div className="live-empty">Loading…</div>

  const me = personOf(session)
  if (me?.assessesAiPosture !== true) return <Navigate to="/recommendations" replace />

  if (me.monitoringActive !== true) {
    return (
      <div className="live-empty">
        <p>
          AI assessment is part of the Monitor plan. On your current plan Defenssive does not assess your Azure AI
          resources, agents or AI applications, so nothing here has been assessed.
        </p>
        <p>
          <Link to="/plans">See plans</Link>
        </p>
      </div>
    )
  }

  return <>{children}</>
}

/** Under the AI findings: the way to the AI assets page, where the inventory now lives (owner, 2026-10-12). */
function AiAssetsLink() {
  return (
    <Card title="Your AI assets" style={{ marginTop: 'var(--space-4)' }}>
      <p className="settings-note" style={{ marginTop: 0 }}>
        Your Azure OpenAI and AI Foundry resources, AI applications from other publishers and agents, with what each can
        reach and the findings about it, are listed together under AI assets.
      </p>
      <p className="settings-note">
        <Link to="/ai-assets">See your AI assets</Link>
      </p>
    </Card>
  )
}
